import { Component, ElementRef, SimpleChanges, ViewChild } from '@angular/core';
import { ComponentModalBase } from '@framework/base';
import { TagSettingViewModel, TagSettingModalViewModal, StepTagSettingViewModel } from '@modules/company-admin/store/models/store-view-models';
import { UntypedFormGroup, UntypedFormBuilder, UntypedFormControl } from '@angular/forms';
import { CreateTagInputApiModel } from '@modules/story/models/api-models/input';
import { storyStepActions } from '@modules/story/store/actions';
import { Store } from '@ngrx/store';
import { CoreState } from '@app/store/core/states';
import { storyStepSelector } from '@modules/story/store/selectors';
import { Observable, Subscription } from 'rxjs';
import { Actions, ofType } from '@ngrx/effects';
import { UpdateStepTagSettingInputApiModel } from '@modules/company-admin/store/models/api-models/input';
import { CompanyHttpService } from '@modules/story/services';
import { AuthService, ModalService } from '@framework/services';
import { ToastrService } from 'ngx-toastr';
import { ConfirmationModalComponent, ConfirmationModalDataModel } from '@modules/shared/components';
import { UploadTagsCsvModalComponent, UploadTagsCsvModalData, UploadTagsCsvModalResult } from '../upload-tags-csv-modal';
import { UploadPromptsCsvModalComponent, UploadPromptsCsvModalData } from '../upload-prompts-csv-modal';
import { PROMPT_AUDIENCE_OPTIONS, promptAudience } from '@modules/shared/utils/prompt-audience.util';

@Component({
  selector: 'app-create-tags-modal',
  templateUrl: './create-tags-modal.component.html',
  styleUrls: ['./create-tags-modal.component.css']
})
export class CreateTagsModalComponent extends ComponentModalBase<TagSettingModalViewModal> {
  private selectStepId$: Observable<number>;
  private selectStoryStep$: Observable<any>;
  private selectStepIdSubscription: Subscription;
  private updateSuccessSubscription: Subscription;
  private updateFailureSubscription: Subscription;
  public showEditWarning:boolean = false;
  public saveError = '';
  private createdAny = false;
  private steps: StepTagSettingViewModel[] = [];
  private formChangeSubscription: Subscription;
  private createQuestionSuccessSubscription: Subscription;
  private createQuestionFailureSubscription: Subscription;
  private updateQuestionSuccessSubscription: Subscription;
  private updateQuestionFailureSubscription: Subscription;
  private selectStoryStepSubscription: Subscription;
  private stepId: number;
  public storeTags : any[] = [];
  public isUpdating: boolean = false;
  // t_story_questions.question_text is varchar(255)
  public readonly maxPromptLength = 255;
  public audienceOptions = PROMPT_AUDIENCE_OPTIONS;
  public isImporting = false;
  public updateForm: UntypedFormGroup;
  public tagChips: string[] = [];
  public tagDraft = '';
  public chipBoxExpanded = false;
  public chipsOverflow = false;
  @ViewChild('chipList') private chipList: ElementRef<HTMLElement>;
  public editingChipIndex: number | null = null;
  public editingChipValue = '';
  private chipEditCancelled = false;

  constructor(
    private formBuilder: UntypedFormBuilder,
    private store$: Store<CoreState>,
    private actions$: Actions,
    private companyHttpService: CompanyHttpService,
    private authService: AuthService,
    private modalService: ModalService,
    private toastService: ToastrService
  ) {
    super();
    this.initForm();
    this.selectStepId$ = this.store$.select(storyStepSelector.selectSelectedStepId);
    this.selectStoryStep$ = this.store$.select(storyStepSelector.selectStorySteps);
  }

  protected onInit(): void {
    if(this.initialState.fromTag){
      if (this.initialState.data) {
        this.updateForm.controls.title.setValue(this.initialState.data.name);
      }
    }
    else{
      if (this.initialState.data) {
        this.updateForm.controls.title.setValue(this.initialState.data.question_text);
      }
      if (this.initialState.showAudience) {
        this.updateForm.controls.audience.setValue(
          this.initialState.data ? promptAudience(this.initialState.data) : 'both'
        );
      }
    }

    this.subscribe();
  }

  protected onDestroy(): void {
    this.unsubscribe();
  }

  protected onChanges(changes: SimpleChanges): void {

  }

  protected onDoCheck(): void {

  }

  protected onAfterContentInit(): void {

  }

  protected onAfterContentChecked(): void {

  }

  protected onAfterViewInit(): void {

  }

  protected onAfterViewChecked(): void {
    this.measureChipOverflow();

  }

  private subscribe() {
    this.selectStepIdSubscription = this.selectStepId$.subscribe((stepId) => {
      if (stepId) {
        this.stepId = stepId;
        this.refreshStoreTags();
      }
    });

    this.formChangeSubscription = this.updateForm.controls.title.valueChanges.subscribe((res)=>{
      this.saveError = '';
      if(res && !this.initialState.fromTag && this.initialState.data?.story_count > 0 && (res.toLowerCase() != this.initialState.data?.question_text.toLowerCase())){
        this.showEditWarning = true;
      }
      else{
        this.showEditWarning = false;
      }

    })

    this.selectStoryStepSubscription = this.selectStoryStep$.subscribe((result: Array<StepTagSettingViewModel>) => {
      this.steps = result || [];
      this.refreshStoreTags();
    });

    this.updateSuccessSubscription = this.actions$.pipe(
      ofType<storyStepActions.UpdateStepTagSuccess>(storyStepActions.ActionTypes.UPDATE_STEP_TAG_SETTING_SUCCESS)
    ).subscribe(() => {
      this.isUpdating = false;
      this.onClose.emit(true);
    });

    this.updateFailureSubscription = this.actions$.pipe(
      ofType<storyStepActions.UpdateStepTagFailure>(storyStepActions.ActionTypes.UPDATE_STEP_TAG_SETTING_FAIL)
    ).subscribe((action) => {
      this.isUpdating = false;
      this.saveError = this.errorMessage(action.error);
    });

    this.createQuestionSuccessSubscription = this.actions$.pipe(
      ofType<storyStepActions.CreateQuestionSuccess>(storyStepActions.ActionTypes.CREATE_QUESTION_SUCCESS)
    ).subscribe((action) => {
      this.isUpdating = false;
      // The prompt already existed for another audience, so the server added this audience to it
      if (action && action.response && action.response.merged) {
        this.toastService.success('That prompt already existed, so it is now available to users and guests.');
      }
      this.onClose.emit(true);
    });

    this.createQuestionFailureSubscription = this.actions$.pipe(
      ofType<storyStepActions.CreateQuestionFailure>(storyStepActions.ActionTypes.CREATE_QUESTION_FAILURE)
    ).subscribe((action) => {
      this.isUpdating = false;
      this.saveError = this.promptErrorMessage(action && action.error);
    });

    this.updateQuestionSuccessSubscription = this.actions$.pipe(
      ofType<storyStepActions.UpdateQuestionSuccess>(storyStepActions.ActionTypes.UPDATE_QUESTION_SUCCESS)
    ).subscribe(() => {
      this.isUpdating = false;
      this.onClose.emit(true);
    });

    this.updateQuestionFailureSubscription = this.actions$.pipe(
      ofType<storyStepActions.UpdateQuestionFailure>(storyStepActions.ActionTypes.UPDATE_QUESTION_FAIL)
    ).subscribe((action) => {
      this.isUpdating = false;
      this.saveError = this.promptErrorMessage(action && action.error);
    });
  }

  private unsubscribe() {
    this.selectStepIdSubscription.unsubscribe();
    this.selectStoryStepSubscription.unsubscribe();
    this.formChangeSubscription.unsubscribe();
    this.updateSuccessSubscription.unsubscribe();
    this.updateFailureSubscription.unsubscribe();
    this.createQuestionSuccessSubscription.unsubscribe();
    this.createQuestionFailureSubscription.unsubscribe();
    this.updateQuestionSuccessSubscription.unsubscribe();
    this.updateQuestionFailureSubscription.unsubscribe();
  }

  private initForm() {
    this.updateForm = this.formBuilder.group({
      title: new UntypedFormControl(''),
      is_active: new UntypedFormControl(true),
      audience: new UntypedFormControl('both')
    })
  }

  public close() {
    this.onClose.emit(this.createdAny);
  }

  get useChips(): boolean {
    return !!(this.initialState && this.initialState.fromTag && !this.initialState.data);
  }

  // Chip names with the one being edited swapped for its live (unsaved) value,
  // so duplicate checks react while typing. Index positions are preserved.
  private get liveChipNames(): string[] {
    if (this.editingChipIndex === null) {
      return this.tagChips;
    }
    return this.tagChips.map((name, index) => index === this.editingChipIndex ? (this.editingChipValue || '').trim() : name);
  }

  get chipStates(): { name: string, invalid: boolean }[] {
    const seen: { [key: string]: boolean } = {};
    return this.liveChipNames.map((name) => {
      if (!name) {
        return { name, invalid: false };
      }
      const key = name.toLowerCase();
      const existing = this.existingTagNames([name]).length > 0;
      const repeated = !!seen[key];
      seen[key] = true;
      return { name, invalid: existing || repeated };
    });
  }

  get invalidChipNotice(): { title: string, detail: string } | null {
    const invalid = this.chipStates.filter((chip) => chip.invalid);
    if (!invalid.length) {
      return null;
    }
    const names = [];
    invalid.forEach((chip) => {
      if (names.indexOf(chip.name) === -1) {
        names.push(chip.name);
      }
    });
    const name = names[0];
    const existing = this.existingTagNames([name]).length > 0;
    if (names.length > 1) {
      return {
        title: 'Some tags need to be removed.',
        detail: 'Remove them to save the other tags.'
      };
    }
    return {
      title: existing ? name + ' already exists.' : name + ' is listed more than once.',
      detail: 'Remove it to save the other tags.'
    };
  }

  public onChipDraftChange(value: string, input?: HTMLInputElement) {
    this.tagDraft = value || '';
    if (this.tagDraft.indexOf(',') !== -1) {
      this.commitDraft();
      // tagDraft goes '' -> '' within one change, so [ngModel] won't clear the box; do it here.
      if (input) {
        input.value = '';
      }
    }
  }

  public onChipKeydown(event: KeyboardEvent) {
    if (event.key === 'Enter' || event.key === ',') {
      event.preventDefault();
      this.commitDraft();
      return;
    }
    if (event.key === 'Backspace' && !this.tagDraft && this.tagChips.length) {
      this.tagChips = this.tagChips.slice(0, -1);
    }
  }

  public onChipPaste(event: ClipboardEvent) {
    const text = event.clipboardData ? event.clipboardData.getData('text') : '';
    if (!text || text.indexOf(',') === -1) {
      return;
    }
    event.preventDefault();
    this.addChipNames(text.split(','));
    this.tagDraft = '';
  }

  public commitDraft() {
    if (!this.tagDraft || !this.tagDraft.trim()) {
      this.tagDraft = '';
      return;
    }
    this.addChipNames(this.tagDraft.split(','));
    this.tagDraft = '';
  }

  public trackByIndex(index: number): number {
    return index;
  }

  get chipEditWidth(): number {
    return Math.max((this.editingChipValue || '').length, 1) + 1;
  }

  public startChipEdit(index: number, event: Event) {
    const target = event.target as HTMLElement;
    if (target && target.closest && target.closest('.tag-chip__remove')) {
      return;
    }
    event.stopPropagation();
    if (this.editingChipIndex === index) {
      return;
    }
    this.commitDraft();
    this.editingChipIndex = index;
    this.editingChipValue = this.tagChips[index] || '';
    setTimeout(() => {
      const input = document.querySelector('.tag-chip__edit') as HTMLInputElement;
      if (input) {
        input.focus();
        input.select();
      }
    });
  }

  public onChipEditKeydown(event: KeyboardEvent) {
    if (event.key === 'Enter') {
      event.preventDefault();
      (event.target as HTMLInputElement).blur();
      return;
    }
    if (event.key === 'Escape') {
      event.preventDefault();
      event.stopPropagation();
      this.chipEditCancelled = true;
      (event.target as HTMLInputElement).blur();
    }
  }

  public commitChipEdit() {
    if (this.chipEditCancelled) {
      this.chipEditCancelled = false;
      this.editingChipIndex = null;
      this.editingChipValue = '';
      return;
    }
    if (this.editingChipIndex === null) {
      return;
    }
    const index = this.editingChipIndex;
    const parts = (this.editingChipValue || '').split(',').map((part) => part.trim()).filter((part) => !!part);
    const next = this.tagChips.slice();
    if (!parts.length) {
      next.splice(index, 1);
    } else {
      next.splice(index, 1, ...parts);
    }
    this.tagChips = next;
    this.editingChipIndex = null;
    this.editingChipValue = '';
  }

  public removeChip(index: number, event?: Event) {
    if (event) {
      event.stopPropagation();
    }
    if (this.editingChipIndex === index) {
      this.chipEditCancelled = true;
      this.editingChipIndex = null;
      this.editingChipValue = '';
    } else if (this.editingChipIndex !== null && index < this.editingChipIndex) {
      this.editingChipIndex -= 1;
    }
    this.tagChips = this.tagChips.filter((_, chipIndex) => chipIndex !== index);
  }

  public removeInvalidChips() {
    const states = this.chipStates;
    this.tagChips = this.tagChips.filter((_, index) => !states[index].invalid);
  }

  public toggleChipBox(event: Event) {
    event.stopPropagation();
    this.chipBoxExpanded = !this.chipBoxExpanded;
  }

  // True when the chips need more than the collapsed height (4 rows).
  private measureChipOverflow() {
    const list = this.chipList && this.chipList.nativeElement;
    let overflow = false;
    if (list) {
      const chip = list.querySelector('.tag-chip') as HTMLElement;
      const gap = parseFloat(getComputedStyle(list).rowGap) || 0;
      const fourRows = chip ? chip.offsetHeight * 4 + gap * 3 : 0;
      overflow = !!chip && list.scrollHeight > fourRows + 1;
    }
    if (overflow !== this.chipsOverflow) {
      setTimeout(() => {
        this.chipsOverflow = overflow;
        if (!overflow) {
          this.chipBoxExpanded = false;
        }
      });
    }
  }

  private scrollChipsToEnd() {
    setTimeout(() => {
      const list = this.chipList && this.chipList.nativeElement;
      if (list && !this.chipBoxExpanded) {
        list.scrollTop = list.scrollHeight;
      }
    });
  }

  private addChipNames(parts: string[]) {
    const next = this.tagChips.slice();
    parts.forEach((part) => {
      const name = (part || '').trim();
      if (name) {
        next.push(name);
      }
    });
    this.tagChips = next;
    this.scrollChipsToEnd();
  }

  get showImport(): boolean {
    return !!(this.initialState && this.initialState.allowImport && this.initialState.fromTag && !this.initialState.data);
  }

  get showCopy(): boolean {
    return this.showImport && !this.initialState.importQuestionId;
  }

  get copyLabel(): string {
    return this.initialState && this.initialState.inbound ? 'From user stories' : 'From guest stories';
  }

  get showPromptImport(): boolean {
    return !!(this.initialState && !this.initialState.fromTag && this.initialState.showAudience && !this.initialState.data);
  }

  public openPromptImport() {
    if (this.isUpdating) {
      return;
    }
    const data: UploadPromptsCsvModalData = { companyId: this.initialState.companyId };
    // Escape and backdrop clicks would hide the modal without telling us, so only its own buttons close it
    this.modalService.showModal(UploadPromptsCsvModalComponent, data, { class: 'modal-lg', backdrop: 'static', keyboard: false }).then((changed: boolean) => {
      if (changed) {
        // Prompts were imported, so close Create too and let the list reload
        this.createdAny = true;
        this.close();
      }
    });
  }

  public openCsvUpload() {
    if (this.isImporting || !this.initialState || !this.initialState.companyId) {
      return;
    }
    if (!this.importQuestionId()) {
      this.toastService.error('Choose a tag filter first.');
      return;
    }
    // Admin-only tags (a fixed importQuestionId) have no user/guest counterpart to mirror to.
    const data: UploadTagsCsvModalData = { inbound: !!this.initialState.inbound, allowMirror: !this.initialState.importQuestionId };
    this.modalService.showModal(UploadTagsCsvModalComponent, data).then((result: UploadTagsCsvModalResult) => {
      if (result && result.file) {
        this.uploadCsv(result.file, result.mirror);
      }
    });
  }

  private uploadCsv(file: File, mirror: boolean) {
    const questionId = this.importQuestionId();
    this.isImporting = true;
    this.withAuthorization((authorization) => {
      this.companyHttpService.bulkUploadAdminTags(this.initialState.companyId, questionId, file, authorization, !!this.initialState.inbound, mirror).subscribe((result: any) => {
        this.isImporting = false;
        this.createdAny = true;
        const created = result?.created || 0;
        const updated = result?.updated || 0;
        const skipped = result?.skipped || [];
        if (created || updated) {
          const parts = [];
          if (created) {
            parts.push(created === 1 ? 'Added 1 tag' : `Added ${created} tags`);
          }
          if (updated) {
            parts.push(updated === 1 ? 'updated 1 tag' : `updated ${updated} tags`);
          }
          this.toastService.success(parts.join(', ') + '.');
        } else {
          this.toastService.warning('No tags were added.');
        }
        this.showMirrorResult(result?.mirror);
        if (skipped.length) {
          const details = skipped.map((item) => `Row ${item.row}: ${item.reason}`).join(' ');
          this.toastService.warning(`${skipped.length === 1 ? '1 row skipped' : skipped.length + ' rows skipped'}. ${details}`);
        }
        this.refreshImportedTags(authorization);
      }, (error) => {
        this.isImporting = false;
        const message = error?.error?.message || error?.error?.detail || 'Could not upload tags.';
        this.toastService.error(message);
      });
    }, () => {
      this.isImporting = false;
      this.toastService.error('Could not upload tags.');
    });
  }

  private showMirrorResult(mirror: any) {
    if (!mirror) {
      return;
    }
    const otherLabel = this.initialState.inbound ? 'user stories' : 'guest stories';
    if (mirror.missing) {
      this.toastService.warning(`Tags were not added to ${otherLabel} because they do not have this tag filter.`);
      return;
    }
    const created = mirror.created || 0;
    const updated = mirror.updated || 0;
    if (!created && !updated) {
      this.toastService.info(`Tags already match on ${otherLabel}.`);
      return;
    }
    const parts = [];
    if (created) {
      parts.push(created === 1 ? 'added 1 tag' : `added ${created} tags`);
    }
    if (updated) {
      parts.push(updated === 1 ? 'updated 1 tag' : `updated ${updated} tags`);
    }
    this.toastService.success(`On ${otherLabel}: ${parts.join(', ')}.`);
  }

  public copyTags() {
    if (this.isImporting || !this.initialState || !this.initialState.companyId) {
      return;
    }
    const inbound = !!this.initialState.inbound;
    const initState: ConfirmationModalDataModel = inbound ? {
      title: 'Import user story tags?',
      message: 'Tags from user stories that guest stories do not have yet will be added to the same tag filter. Tags already here stay as they are and nothing is removed.',
      yesButtonText: 'Import',
      cancelButtonText: 'Cancel'
    } : {
      title: 'Import guest story tags?',
      message: 'Tags from guest stories that user stories do not have yet will be added to the same tag filter. Tags already here stay as they are and nothing is removed.',
      yesButtonText: 'Import',
      cancelButtonText: 'Cancel'
    };
    this.modalService.showModal(ConfirmationModalComponent, initState).then((isConfirm: boolean) => {
      if (isConfirm) {
        this.runCopy(inbound ? 'user-to-guest' : 'guest-to-user', inbound ? 'user story tags' : 'guest story tags');
      }
    });
  }

  private runCopy(direction: 'user-to-guest' | 'guest-to-user', sourceLabel: string) {
    this.isImporting = true;
    this.withAuthorization((authorization) => {
      this.companyHttpService.copyStoryTagSettings(this.initialState.companyId, direction, authorization).subscribe((result: any) => {
        this.isImporting = false;
        this.createdAny = true;
        this.toastService.success(this.copyResultMessage(result, sourceLabel));
        this.refreshImportedTags(authorization);
      }, (error) => {
        this.isImporting = false;
        const message = error?.error?.message || error?.error?.detail || 'Could not import tags.';
        this.toastService.error(message);
      });
    }, () => {
      this.isImporting = false;
      this.toastService.error('Could not import tags.');
    });
  }

  private importQuestionId(): number {
    return this.initialState && this.initialState.importQuestionId ? this.initialState.importQuestionId : this.stepId;
  }

  private refreshImportedTags(authorization: string) {
    const inbound = !!(this.initialState && this.initialState.inbound);
    this.companyHttpService.getStepsAuthorized(this.initialState.companyId, authorization, inbound).subscribe((steps) => {
      this.store$.dispatch(new storyStepActions.LoadSuccess(steps));
    });
  }

  private withAuthorization(onReady: (authorization: string) => void, onMissing: () => void) {
    const gsToken = this.authService.getGsToken();
    if (gsToken) {
      onReady(`GSeeker ${gsToken}`);
      return;
    }
    this.authService.cognito.getIdToken().then((token) => {
      onReady(`Bearer ${token}`);
    }).catch(() => onMissing());
  }

  private copyResultMessage(result: any, sourceLabel: string) {
    const created = result?.created || 0;
    const existing = result?.existing || 0;
    if (!created) {
      return existing ? `All ${sourceLabel} are already here.` : `No ${sourceLabel} to import.`;
    }
    const added = created === 1 ? 'Added 1 tag' : `Added ${created} tags`;
    const kept = existing ? ` ${existing === 1 ? '1 was' : existing + ' were'} already here.` : '';
    return `Imported ${sourceLabel}. ${added}.${kept}`;
  }

  private refreshStoreTags() {
    const questionId = this.importQuestionId();
    if (!this.initialState.fromTag || !questionId || !this.steps.length) {
      return;
    }
    const step = this.steps.find((item) => item.id == questionId && item.type === 'tag')
      || this.steps.find((item) => item.id == questionId);
    this.storeTags = step && step.tags ? step.tags : [];
  }

  private parsedTags(): { names: string[], repeated: string[] } {
    const raw = this.useChips
      ? this.liveChipNames.join(',')
      : (this.updateForm.controls.title.value || '').toString();
    const splitNames = this.initialState.fromTag && !this.initialState.data;
    const parts = splitNames ? raw.split(',') : [raw];
    const names: string[] = [];
    const seen: { [key: string]: string } = {};
    const repeated: string[] = [];
    parts.forEach((part) => {
      const name = part.trim();
      if (!name) {
        return;
      }
      const key = name.toLowerCase();
      if (seen[key]) {
        if (repeated.indexOf(seen[key]) === -1) {
          repeated.push(seen[key]);
        }
        return;
      }
      seen[key] = name;
      names.push(name);
    });
    return { names, repeated };
  }

  private existingTagNames(names: string[]): string[] {
    const currentId = this.initialState.data && this.initialState.data.id;
    return names.filter((name) => this.storeTags.some((tag) => {
      if (currentId && tag.id === currentId) {
        return false;
      }
      return (tag.name || '').trim().toLowerCase() === name.toLowerCase();
    }));
  }

  get duplicateError(): string {
    if (!this.initialState || !this.initialState.fromTag) {
      return '';
    }
    const parsed = this.parsedTags();
    const existing = this.existingTagNames(parsed.names);
    const messages = [];
    if (existing.length === 1) {
      messages.push(existing[0] + ' already exists.');
    } else if (existing.length > 1) {
      messages.push(existing.join(', ') + ' already exist.');
    }
    if (parsed.repeated.length === 1) {
      messages.push(parsed.repeated[0] + ' is listed more than once.');
    } else if (parsed.repeated.length > 1) {
      messages.push(parsed.repeated.join(', ') + ' are listed more than once.');
    }
    return messages.join(' ');
  }

  get fieldLabel(): string {
    if (!this.initialState.fromTag) {
      return 'Enter a new story prompt';
    }
    return this.initialState.data ? 'Tag' : 'Tags';
  }

  get fieldPlaceholder(): string {
    if (!this.initialState.fromTag) {
      return 'Title';
    }
    return this.initialState.data ? 'Tag' : 'abc, xyz';
  }

  // Prompt saves can fail on a field (too long, blank) or with a message (already exists)
  private promptErrorMessage(error: any): string {
    const body = error && error.error;
    if (body && typeof body === 'object') {
      const fieldError = body.question_text || body.audience || body.non_field_errors || body.message || body.detail;
      if (Array.isArray(fieldError) && fieldError.length) {
        return String(fieldError[0]);
      }
      if (typeof fieldError === 'string') {
        return fieldError;
      }
    }
    return 'Could not save that prompt. Please try again.';
  }

  get promptLength(): number {
    return ((this.updateForm.controls.title.value || '') + '').trim().length;
  }

  get promptTooLong(): boolean {
    return !!(this.initialState && !this.initialState.fromTag && this.promptLength > this.maxPromptLength);
  }

  private errorMessage(error: any): string {
    const body = error && (error.error || error);
    if (!body) {
      return 'Could not save that tag.';
    }
    if (typeof body === 'string') {
      return body;
    }
    const message = body.message || body.detail || body;
    if (Array.isArray(message)) {
      return message.join(' ');
    }
    if (typeof message === 'string') {
      return message;
    }
    return 'Could not save that tag.';
  }

  // All new tags go in one request; sending them one at a time made 100 tags take minutes.
  private createTag() {
    const names = this.parsedTags().names;
    this.companyHttpService.createStepTagSettings(this.initialState.companyId, this.importQuestionId(), !!this.initialState.inbound, names).subscribe(() => {
      this.isUpdating = false;
      this.createdAny = true;
      this.onClose.emit(true);
    }, (error) => {
      this.isUpdating = false;
      this.saveError = this.errorMessage(error);
    });
  }

  private UpdateTag() {
    const request: UpdateStepTagSettingInputApiModel = {
      text: (this.updateForm.value.title || '').trim()
    }
    this.store$.dispatch(new storyStepActions.UpdateStepTag(this.initialState.companyId, this.initialState.data.id, this.initialState.inbound, false, request));
  }

  private createQuestion() {
    if(!this.initialState.main_board){
      const createTagModel: any = {
        question_text: this.updateForm.value.title,
        inbound: this.initialState.inbound
      };
      if (this.initialState.showAudience) {
        delete createTagModel.inbound;
        createTagModel.audience = this.updateForm.value.audience;
      }
      this.store$.dispatch(new storyStepActions.CreateQuestion(this.initialState.companyId,createTagModel));
    }else{
      const createTagModel: any = {
        question_text: this.updateForm.value.title,
        inbound: this.initialState.inbound,
        is_active:this.updateForm.value.is_active ? 1 : 0
      }; 
      this.store$.dispatch(new storyStepActions.CreateQuestion(this.initialState.companyId,createTagModel));
    }

    
  }

  private UpdateQuestion() {
    // Only send an audience when the admin picked one; editing the text elsewhere must not
    // turn a prompt for users and guests back into a single-audience prompt.
    const request: any = {
      question_text: this.updateForm.value.title
    }
    if (this.initialState.showAudience) {
      request.audience = this.updateForm.value.audience;
    }
    this.store$.dispatch(new storyStepActions.UpdateQuestion(this.initialState.companyId,this.initialState.data.id,request));
  }



  public save() {
    this.saveError = '';
    if (this.useChips) {
      this.commitDraft();
    }
    if(this.IsDisabled) { return;}
    this.isUpdating = true;
    if(this.initialState.fromTag){
      if (this.initialState.data) {
        this.UpdateTag();
      } else {
        this.createTag();
      }
    }
    else{
      if (this.initialState.data) {
        this.UpdateQuestion();
      } else {
        this.createQuestion();
      }
    }

  }

  get IsDisabled(): boolean {
    if (this.initialState && this.initialState.fromTag) {
      const parsed = this.parsedTags();
      return !parsed.names.length || !!this.duplicateError;
    }
    return !this.updateForm.controls.title.value || this.promptTooLong;
  }
}
